/**
 * Task Execution Page
 * Main UI for the Task Execution Framework - handles all phases of task execution
 * from container setup through PR creation with user interaction points.
 * Step granularity comes from the backend TaskStepTracker (same pattern as analysis).
 */

import { ArrowLeft, CheckCircle2, Copy, Check, ExternalLink, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmModal } from '../components/ConfirmModal';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DisconnectedBanner } from '../components/analysis/DisconnectedBanner';
import { WorkflowStepList } from '../components/analysis/WorkflowStepList';
import Footer from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { QuestionForm } from '../components/tasks/QuestionForm';
import { ReviewPanel } from '../components/tasks/ReviewPanel';
import { useSocket } from '../contexts/socketTypes';
import { TaskPhase, useTaskExecution } from '../hooks/useTaskExecution';
import { analytics } from '../services/analytics/posthog';
import { logger } from '../utils/logger';

// Phases where the user must act (scroll target)
const USER_INPUT_PHASES = new Set([TaskPhase.WAITING_FOR_INPUT, TaskPhase.WAITING_FOR_REVIEW]);

export function TaskExecutionPage() {
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const { isConnected, hasConnectedOnce } = useSocket();

  const [copied, setCopied] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const phaseContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    analytics.pageViewed('task_execution');
  }, []);

  // TaskId is required - if missing, redirect to home
  useEffect(() => {
    if (!taskId) {
      logger.error('Task ID is required');
      navigate('/');
    }
  }, [taskId, navigate]);

  const {
    taskStatus,
    loading,
    error,
    taskTypes,
    submitAnswers,
    submitReview,
    reviseAnswers,
    cancelTask,
    lastAnswers,
  } = useTaskExecution(taskId);

  // Scroll to phase content when entering a user-input phase
  useEffect(() => {
    if (taskStatus?.phase && USER_INPUT_PHASES.has(taskStatus.phase)) {
      phaseContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [taskStatus?.phase]);

  const handleCopyWorkflowId = useCallback(() => {
    if (!taskStatus?.workflowId) return;
    navigator.clipboard.writeText(taskStatus.workflowId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [taskStatus?.workflowId]);

  // Compute repository URL for back links
  const repositoryUrl = taskStatus?.repositoryId
    ? `/repository/${taskStatus.repositoryId}`
    : '/dashboard';

  const handleCancel = () => setShowCancelModal(true);

  const handleCancelConfirm = async () => {
    await cancelTask();
    navigate(repositoryUrl);
  };

  // Render loading state
  const renderLoadingState = (message: string) => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">{message}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">This may take a few minutes...</p>
        </div>
      </div>
    );
  };

  // Render success state with PR link
  const renderSuccessState = () => {
    // Extract PR URL from workflow outputs
    const prUrl = taskStatus?.outputs?.pr_url;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Task Completed Successfully!
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            All changes have been verified and a pull request has been created.
          </p>

          {prUrl && (
            <a
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-800 mb-4"
            >
              View Pull Request
              <ExternalLink className="w-4 h-4" />
            </a>
          )}

          <Link
            to={repositoryUrl}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Back to Repository
          </Link>
        </div>
      </div>
    );
  };

  // Render error state
  const renderErrorState = () => {
    // Combine hook error with taskStatus errors for complete error display
    const allErrors = [...(error ? [error] : []), ...(taskStatus?.errors || [])].filter(
      (e, i, arr) => arr.indexOf(e) === i,
    ); // dedupe

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center shrink-0">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Task Failed
            </h3>
            {allErrors.length > 0 ? (
              <ul className="text-sm text-gray-600 dark:text-gray-400 mb-4 list-disc list-inside space-y-1">
                {allErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                An unknown error occurred.
              </p>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-800"
              >
                Retry
              </button>
              <Link
                to={repositoryUrl}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-800 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Back to Repository
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render cancelled state (user rejected changes)
  const renderCancelledState = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center shrink-0">
          <svg
            className="w-6 h-6 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Task Cancelled
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Changes were rejected and no pull request was created.
          </p>
          <Link
            to={repositoryUrl}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-800 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Back to Repository
          </Link>
        </div>
      </div>
    </div>
  );

  // Render phase-appropriate content
  const renderPhaseContent = () => {
    if (!taskStatus) {
      return renderLoadingState('Initializing task...');
    }

    const phase = taskStatus.phase;

    switch (phase) {
      case TaskPhase.ANALYSIS:
        return renderLoadingState('Analyzing repository...');

      case TaskPhase.GENERATE_QUESTIONS:
        return renderLoadingState('Preparing configuration questions...');

      case TaskPhase.WAITING_FOR_INPUT:
        return taskStatus.questions ? (
          <QuestionForm
            questions={taskStatus.questions}
            onSubmit={submitAnswers}
            loading={loading}
            initialAnswers={lastAnswers}
          />
        ) : (
          renderLoadingState('Loading questions...')
        );

      case TaskPhase.PLANNING:
        return renderLoadingState('Creating transformation plan...');

      case TaskPhase.APPLY_CHANGES:
        return renderLoadingState('Applying changes to repository...');

      case TaskPhase.VERIFICATION:
        return renderLoadingState('Running tests and validation...');

      case TaskPhase.WAITING_FOR_REVIEW:
        return (
          <ReviewPanel
            preview={taskStatus.preview}
            verification={taskStatus.verification}
            onSubmitReview={submitReview}
            onReviseAnswers={reviseAnswers}
            loading={loading}
          />
        );

      case TaskPhase.DECISION:
        return renderLoadingState('Processing your decision...');

      case TaskPhase.CREATE_PR:
        return taskStatus.status === 'completed'
          ? renderSuccessState()
          : renderLoadingState('Creating pull request...');

      default:
        return renderLoadingState('Processing...');
    }
  };

  // Get display name for task class from backend-provided taskTypes
  const getTaskDisplayName = () => {
    const taskClass = taskStatus?.taskClass || taskStatus?.type;
    if (!taskClass) return 'Task Execution';

    const match = taskTypes.find((t) => t.taskClass === taskClass);
    return match?.displayName || taskClass;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Navbar pageTitle={getTaskDisplayName()} showDivider={true} />

      <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <Link
                to={repositoryUrl}
                className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Repository
              </Link>

              {taskStatus?.workflowId && (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                    Workflow ID: {taskStatus.workflowId}
                  </p>
                  <button
                    onClick={handleCopyWorkflowId}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    title="Copy Workflow ID"
                  >
                    {copied ? (
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {taskStatus && !['completed', 'failed', 'cancelled'].includes(taskStatus.status) && (
              <button
                onClick={handleCancel}
                className="shrink-0 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-gray-800 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
              >
                Cancel Task
              </button>
            )}
          </div>

          {/* Connection Status Banner */}
          {hasConnectedOnce && !isConnected && <DisconnectedBanner />}

          {/* Phase Content (above progress so actionable content is visible first) */}
          <div ref={phaseContentRef} />
          {taskStatus?.status === 'cancelled'
            ? renderCancelledState()
            : taskStatus?.status === 'failed' ||
                taskStatus?.outputs?.success === false ||
                (taskStatus?.errors && taskStatus.errors.length > 0)
              ? renderErrorState()
              : renderPhaseContent()}

          {/* Progress Steps (from backend step tracker) */}
          {taskStatus?.workflow?.steps && taskStatus.workflow.steps.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Progress
                  {taskStatus.iteration > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (Iteration {taskStatus.iteration + 1})
                    </span>
                  )}
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {taskStatus.workflow.percent}% complete
                </span>
              </div>
              <WorkflowStepList
                steps={taskStatus.workflow.steps.map((s) => ({ ...s, percent: s.weight }))}
              />
            </div>
          )}

          {/* Error Alert */}
          {error && taskStatus?.status !== 'failed' && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                    Warning
                  </h4>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        title="Cancel Task"
        message="Are you sure you want to cancel this task? Any progress made so far will be discarded."
        confirmText="Cancel Task"
        cancelText="Keep Running"
        variant="danger"
      />
    </div>
  );
}
